import { ActionTree, GetterTree, Module, MutationTree } from "vuex"
import axios from "axios"
import { RootState } from "."
import { WeatherState, LocationResponse } from "@/types/weather"
import { LocationLicenses } from "@/types/location-license"

const state: WeatherState = {
    data: null,
    coordinates: null,
    showLocationModal: false,
    loading: false,
    error: null,
    locationId: null,
    weatherData: null,
    licenses: null,
}

const mutations: MutationTree<WeatherState> = {
    SET_LOADING(state, loading: boolean) {
        state.loading = loading
    },
    SET_DATA(state, data: any) {
        state.data = data
    },
    SET_COORDINATES(state, coordinates: any) {
        state.coordinates = coordinates
    },
    SET_SHOW_LOCATION_MODAL(state, show: boolean) {
        state.showLocationModal = show
    },
    SET_ERROR(state, error: string | null) {
        state.error = error
    },
    SET_LOCATION_ID(state, locationId: number | null) {
        state.locationId = locationId
    },
    SET_WEATHER_DATA(state, weatherData: any) {
        state.weatherData = weatherData
    },
    SET_LICENSES(state, licenses: LocationLicenses | null) {
        state.licenses = licenses
    },
}

const actions: ActionTree<WeatherState, RootState> = {
    async fetchWeather({ commit }, locationId: number) {
        commit("SET_LOADING", true)
        commit("SET_ERROR", null)

        try {
            const response = await axios.get(`/locations/${locationId}/data/weather`)
            commit("SET_WEATHER_DATA", response.data.data)
        } catch (error: any) {
            commit("SET_ERROR", error.message || "Failed to fetch weather data")
        } finally {
            commit("SET_LOADING", false)
        }
    },
    async fetchLocation({ commit }, locationId: number) {
        commit("SET_LOADING", true)
        commit("SET_ERROR", null)

        try {
            const response = await axios.get<LocationResponse>(`/locations/${locationId}`)
            commit("SET_DATA", response.data)

            const coordinates = response.data.data.location.coordinates
            if (coordinates && coordinates.latitude != null && coordinates.longitude != null) {
                commit("SET_COORDINATES", coordinates)
            } else {
                commit("SET_COORDINATES", null)
            }
            commit("SET_LICENSES", response.data.data.licenses ?? null)
        } catch (error: any) {
            commit("SET_ERROR", error.message || "Failed to fetch location data")
        } finally {
            commit("SET_LOADING", false)
        }
    },
    async saveLocationCoordinates({ commit, state }, coordinates) {
        commit("SET_LOADING", true)
        commit("SET_ERROR", null)

        try {
            const locationData = state.data?.data?.location
            if (!locationData) {
                throw new Error("No location data available")
            }
            const putData = {
                data: {
                    name: locationData.name,
                    spotRegion: {
                        type: locationData.eicCode
                            ? "EIC"
                            : locationData.agileRegion
                            ? "AGILE"
                            : null,
                        code:
                            locationData.eicCode?.eic ||
                            locationData.agileRegion?.regionCode ||
                            null,
                    },
                    coordinates: {
                        latitude: coordinates.latitude?.toString() || null,
                        longitude: coordinates.longitude?.toString() || null,
                    },
                    countryCode: locationData.country?.code,
                    timezone: locationData.timezone,
                },
            }

            await axios.put(`/locations/${locationData.id}`, putData)
            commit("SET_COORDINATES", coordinates)
            commit("SET_SHOW_LOCATION_MODAL", false)
        } catch (error: any) {
            commit("SET_ERROR", error.message || "Failed to save coordinates")
        } finally {
            commit("SET_LOADING", false)
        }
    },
}

const getters: GetterTree<WeatherState, RootState> = {
    data: (state) => state.data,
    coordinates: (state) => state.coordinates,
    showLocationModal: (state) => state.showLocationModal,
    loading: (state) => state.loading,
    error: (state) => state.error,
    locationId: (state) => state.locationId,
    weatherData: (state) => state.weatherData,
    licenses: (state) => state.licenses,
}

const WeatherModule: Module<WeatherState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}

export default WeatherModule
